import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { trackEvent } from "@/lib/analytics";

export type ThemeMode = "light" | "dark" | "system";
export type Locale = "tr" | "en";

type AppContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  toggleLocale: () => void;
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  isDark: boolean;
};

const AppContext = createContext<AppContextValue | null>(null);

export function AppProviders({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const stored = localStorage.getItem("ena_lang");
    return stored === "en" ? "en" : "tr";
  });

  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem("ena_theme");
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
    return "dark";
  });

  const [sysDark, setSysDark] = useState(() =>
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );

  const isDark = themeMode === "dark" || (themeMode === "system" && sysDark);

  useEffect(() => {
    localStorage.setItem("ena_lang", locale);
  }, [locale]);

  useEffect(() => {
    localStorage.setItem("ena_theme", themeMode);
  }, [themeMode]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const fn = (e: MediaQueryListEvent) => setSysDark(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", isDark ? "dark" : "light");
  }, [isDark]);

  const setLocale = useCallback((next: Locale) => setLocaleState(next), []);
  const toggleLocale = useCallback(() => {
    setLocaleState((l) => {
      const next = l === "tr" ? "en" : "tr";
      trackEvent("language_change", {
        event_category: "engagement",
        language: next,
      });
      return next;
    });
  }, []);
  const setThemeMode = useCallback((mode: ThemeMode) => setThemeModeState(mode), []);

  const value = useMemo(
    () => ({ locale, setLocale, toggleLocale, themeMode, setThemeMode, isDark }),
    [locale, setLocale, toggleLocale, themeMode, setThemeMode, isDark]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProviders");
  return ctx;
}
